import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  Button,
  Label,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator
} from '@bloomcode/ui';
import { useCookieSession } from '@bloomcode/auth';

export default function UserPreferencesPage() {
  const { isAuthenticated } = useCookieSession();
  
  const [preferences, setPreferences] = useState({
    theme: 'system',
    fontSize: 'medium',
    emailNotifications: true,
    pushNotifications: false,
    contentLanguage: 'en',
    showReadingTime: true,
    autoPlayMedia: false
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSaving(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Sign In Required</h1>
        <p className="text-muted-foreground">Please sign in to manage your preferences.</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Preferences | App CMS</title>
        <meta name="description" content="Manage your display preferences" />
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <h1 className="text-3xl font-bold mb-8">Preferences</h1>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Display Settings</CardTitle>
              <CardDescription>Customize how content is displayed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="theme">Theme</Label>
                  <p className="text-sm text-muted-foreground">Choose your preferred theme</p>
                </div>
                <Select 
                  value={preferences.theme} 
                  onValueChange={(value) => setPreferences(p => ({ ...p, theme: value }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="fontSize">Font Size</Label>
                  <p className="text-sm text-muted-foreground">Adjust text size for readability</p>
                </div>
                <Select 
                  value={preferences.fontSize} 
                  onValueChange={(value) => setPreferences(p => ({ ...p, fontSize: value }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="showReadingTime">Show Reading Time</Label>
                  <p className="text-sm text-muted-foreground">Display estimated reading time on articles</p>
                </div>
                <Switch
                  id="showReadingTime"
                  checked={preferences.showReadingTime}
                  onCheckedChange={(checked) => setPreferences(p => ({ ...p, showReadingTime: checked }))}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="autoPlayMedia">Auto-play Media</Label>
                  <p className="text-sm text-muted-foreground">Automatically play videos and audio</p>
                </div>
                <Switch
                  id="autoPlayMedia"
                  checked={preferences.autoPlayMedia}
                  onCheckedChange={(checked) => setPreferences(p => ({ ...p, autoPlayMedia: checked }))}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notifications</CardTitle>
              <CardDescription>Manage your notification preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="emailNotifications">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive updates via email</p>
                </div>
                <Switch
                  id="emailNotifications"
                  checked={preferences.emailNotifications}
                  onCheckedChange={(checked) => setPreferences(p => ({ ...p, emailNotifications: checked }))}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="pushNotifications">Push Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive browser push notifications</p>
                </div>
                <Switch
                  id="pushNotifications"
                  checked={preferences.pushNotifications}
                  onCheckedChange={(checked) => setPreferences(p => ({ ...p, pushNotifications: checked }))}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Language & Region</CardTitle>
              <CardDescription>Set your language preferences</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="contentLanguage">Content Language</Label>
                  <p className="text-sm text-muted-foreground">Preferred language for content</p>
                </div>
                <Select 
                  value={preferences.contentLanguage} 
                  onValueChange={(value) => setPreferences(p => ({ ...p, contentLanguage: value }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
